// Identifiers that must ALWAYS render with ASCII digits, whatever the UI language:
// phone numbers, payment TRX ids, booking codes, OTPs (round2-i18n.md §6 — the
// admin design's `ASCII_ONLY` guard). People read these aloud, paste them into a
// wallet app or an SMS, and compare them character by character; Bangla digits
// would break every one of those.
//
// Two layers:
//   1. `asciiCode(value)` — an explicit marker. Wrap any identifier before handing
//      it to a formatter. This is the ONLY safe option for all-digit codes (an OTP
//      "482913" is indistinguishable from a number by shape).
//   2. `looksLikeAsciiCode(str)` — a shape check used as a safety net by
//      `localiseDigits`. It is tenant-blind: it matches shapes, never a brand
//      prefix (booking-code prefixes come from the registry, INF-1).

// A marked identifier. A class (not a branded string) so the mark survives at
// runtime and `localiseDigits` can recognise it.
export class AsciiCode {
  readonly value: string;
  constructor(value: string | number) {
    this.value = String(value);
  }
  toString(): string {
    return this.value;
  }
}

export function asciiCode(value: string | number): AsciiCode {
  return new AsciiCode(value);
}

// International phone: "+8801712345678", "+1 415-555-0100".
const INTL_PHONE_RE = /^\+\d[\d\s-]{6,}$/;
// A digit run with a leading zero is never a quantity — local phone numbers and
// zero-padded codes ("01712345678", "048213").
const LEADING_ZERO_RE = /^0\d{5,}$/;
// A single token mixing ASCII letters and digits: booking codes ("XY-4821"), TRX
// ids ("8N7K2Q1A"), reference codes. No whitespace, so prose never matches.
const ALNUM_CODE_RE = /^(?=\S*[A-Za-z])(?=\S*\d)[A-Za-z0-9][A-Za-z0-9._:/#-]*$/;

export function looksLikeAsciiCode(str: string): boolean {
  const s = str.trim();
  return INTL_PHONE_RE.test(s) || LEADING_ZERO_RE.test(s) || ALNUM_CODE_RE.test(s);
}

// True for an explicit `asciiCode()` marker or a string with a code shape.
export function isAsciiCode(value: unknown): boolean {
  if (value instanceof AsciiCode) return true;
  return typeof value === 'string' && looksLikeAsciiCode(value);
}
